import { IS_SERVER } from '@/lib/utils/helper';

export enum BROWSER_DATABASE_KEYS {
  ROWS_PER_PAGE = 'TT_ROWS_PER_PAGE',
  TABLE_PREFERRED_ORDER = 'TT_TABLE_PREFERRED_ORDER',
  SITE_VIEW_PREFERENCES = 'TT_SITE_VIEW_PREFERENCES',
  DISMISSED_BANNER_MESSAGE = 'TT_DISMISSED_BANNER_MESSAGE',
  SELECTED_ORGANISATION = 'TT_SELECTED_ORGANISATION',
}

const browserDatabase = {
  /**
   * Loads data from browser storage
   */
  getItem<T = unknown>(key: BROWSER_DATABASE_KEYS, defaultValue: T | null = null): T | null {
    if (IS_SERVER) {
      console.warn(
        `Tried getting localStorage key “${key}” even though environment is not a client. Returning default value.`,
      );
      return defaultValue;
    }

    try {
      const entryObject = JSON.parse(localStorage.getItem(key) || '');
      const { data, expiration, createdAt } = entryObject;
      const MILLISECONDS_TO_SECONDS = 1000;

      if (expiration && Date.now() - createdAt > expiration * MILLISECONDS_TO_SECONDS) {
        localStorage.removeItem(key);

        return defaultValue;
      }

      return data;
    } catch {
      return defaultValue;
    }
  },

  /**
   * Save data to local storage
   */
  setItem<T = unknown>(key: BROWSER_DATABASE_KEYS, data: T, expiration?: number) {
    if (IS_SERVER) {
      console.warn(
        `Tried setting localStorage key “${key}” even though environment is not a client.`,
      );
    }

    localStorage.setItem(
      key,
      JSON.stringify({
        data,
        expiration,
        createdAt: Date.now(),
      }),
    );
  },

  /**
   * Delete item from local storage
   */
  deleteItem(key: BROWSER_DATABASE_KEYS) {
    if (IS_SERVER) {
      console.warn(
        `Tried deleting localStorage key “${key}” even though environment is not a client.`,
      );
    }
    localStorage.removeItem(key);
  },
};

export default browserDatabase;
